/**
 * useEdgeGeometryEdit - Hook integrador para edición geométrica de edges
 *
 * @version 2.0
 * @created 2026-04-11
 * @updated 2026-04-13
 * @module core/spatial/hooks
 *
 * @description
 * Integra todos los componentes de edición geométrica de segmentos:
 * - Renderiza handles de shape points
 * - Maneja drag de handles existentes
 * - Click sobre el edge para agregar nuevos shape points
 * - Delete/Backspace para eliminar shape points
 * - Conecta con NetworkEditContext
 *
 * Operaciones disponibles en modo "Editar forma":
 * - DRAG shape point → mover punto existente
 * - CLICK sobre la línea → agregar nuevo shape point
 * - DELETE/BACKSPACE → eliminar shape point seleccionado
 * - ESC → salir del modo de edición
 *
 * IMPORTANTE: Ninguna de estas operaciones altera la topología.
 * El edge sigue siendo UN solo segmento entre los mismos nodos.
 *
 * @example
 * ```typescript
 * // En el componente de mapa principal
 * useEdgeGeometryEdit();
 * ```
 */

import { useEffect, useRef, useState } from 'react';
import { useNetworkEdit } from '../contexts/NetworkEditContext';
import { useUrbanNetworkSafe } from '../UrbanNetworkContext';
import { useEdgeShapeHandlesLayer } from './useEdgeShapeHandlesLayer';
import { useEdgeShapePointDrag } from './useEdgeShapePointDrag';
import { useEdgeShapePointAdd } from './useEdgeShapePointAdd';
import { mapEngine } from '../../map-engine/MapEngineService';

/**
 * Hook integrador para edición geométrica de edges
 *
 * Usa este hook en el componente de mapa principal para habilitar
 * la funcionalidad de edición geométrica de segmentos.
 */
export function useEdgeGeometryEdit(): void {
  const {
    geometryEditEdgeId,
    startShapePointDrag,
    updateShapePointPosition,
    confirmGeometryEdit,
    cancelGeometryEdit,
    addShapePoint,
    removeShapePoint,
  } = useNetworkEdit();

  // Estado para trackear el shape point "seleccionado" (último tocado)
  // Usado para saber cuál eliminar con Delete/Backspace
  const [lastTouchedIndex, setLastTouchedIndex] = useState<number | null>(null);

  // Renderizar handles de shape points cuando hay un edge en edición
  useEdgeShapeHandlesLayer({
    edgeId: geometryEditEdgeId,
  });

  // Manejar drag de shape points existentes
  useEdgeShapePointDrag({
    enabled: geometryEditEdgeId !== null,
    onDragStart: (index) => {
      console.log('[useEdgeGeometryEdit] Shape point drag started:', index);
      startShapePointDrag(index);
      setLastTouchedIndex(index); // Trackear el último punto tocado
    },
    onDragMove: (pos) => {
      updateShapePointPosition(pos);
    },
    onDragEnd: async () => {
      console.log('[useEdgeGeometryEdit] Shape point drag ended, confirming...');
      await confirmGeometryEdit();
    },
    onDragCancel: () => {
      console.log('[useEdgeGeometryEdit] Shape point drag cancelled');
      cancelGeometryEdit();
    },
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // AGREGAR SHAPE POINTS: Click sobre el edge
  // ═══════════════════════════════════════════════════════════════════════════
  useEdgeShapePointAdd({
    enabled: geometryEditEdgeId !== null,
    edgeId: geometryEditEdgeId,
    onAddPoint: async (position) => {
      console.log('[useEdgeGeometryEdit] ➕ Adding shape point via click');
      const newIndex = await addShapePoint(position);
      if (newIndex !== null) {
        setLastTouchedIndex(newIndex); // El nuevo punto queda "seleccionado"
      }
      return newIndex;
    },
  });

  // Refs para evitar stale closures
  const cancelGeometryEditRef = useRef(cancelGeometryEdit);
  const removeShapePointRef = useRef(removeShapePoint);
  const lastTouchedIndexRef = useRef(lastTouchedIndex);

  useEffect(() => {
    cancelGeometryEditRef.current = cancelGeometryEdit;
  }, [cancelGeometryEdit]);

  useEffect(() => {
    removeShapePointRef.current = removeShapePoint;
  }, [removeShapePoint]);

  useEffect(() => {
    lastTouchedIndexRef.current = lastTouchedIndex;
  }, [lastTouchedIndex]);

  // ═══════════════════════════════════════════════════════════════════════════
  // KEYBOARD: ESC para salir, Delete/Backspace para eliminar shape point
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    console.log('[useEdgeGeometryEdit] 🎹 Keyboard effect setup, geometryEditEdgeId:', geometryEditEdgeId);

    if (!geometryEditEdgeId) {
      console.log('[useEdgeGeometryEdit] ⏭️ No geometryEditEdgeId, skipping keyboard handler registration');
      return;
    }

    const handleKeyDown = (e: KeyboardEvent): void => {
      // ESC: Salir del modo de edición
      if (e.key === 'Escape') {
        console.log('[GEOMETRY_EXIT] ════════════════════════════════════════════');
        console.log('[GEOMETRY_EXIT] 🔴 ESC EXIT PATH - Calling cancelGeometryEdit');
        console.log('[GEOMETRY_EXIT] ════════════════════════════════════════════');
        e.preventDefault();
        e.stopPropagation();
        cancelGeometryEditRef.current();
        setLastTouchedIndex(null);
        return;
      }

      // Delete/Backspace: Eliminar el último shape point tocado
      if (e.key === 'Delete' || e.key === 'Backspace') {
        const indexToRemove = lastTouchedIndexRef.current;
        if (indexToRemove !== null && indexToRemove > 0) {
          console.log('[useEdgeGeometryEdit] ➖ Removing shape point via Delete/Backspace:', indexToRemove);
          e.preventDefault();
          e.stopPropagation();
          removeShapePointRef.current(indexToRemove);
          setLastTouchedIndex(null); // Limpiar selección después de eliminar
        } else {
          console.log('[useEdgeGeometryEdit] ⏭️ No shape point selected to delete');
        }
        return;
      }
    };

    // Usar capture phase para interceptar antes que otros handlers
    document.addEventListener('keydown', handleKeyDown, { capture: true });
    console.log('[useEdgeGeometryEdit] ✅ Keyboard handlers registered (ESC, Delete, Backspace)');

    return () => {
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
      console.log('[useEdgeGeometryEdit] 🧹 Keyboard handlers removed');
    };
  }, [geometryEditEdgeId]); // Deps: solo geometryEditEdgeId, los demás usan refs

  // Limpiar lastTouchedIndex cuando sale del modo de edición
  useEffect(() => {
    if (!geometryEditEdgeId) {
      setLastTouchedIndex(null);
    }
  }, [geometryEditEdgeId]);

  // Cursor especial cuando está en modo edición geométrica
  useEffect(() => {
    const map = mapEngine.getMap();
    if (!map) return;

    if (geometryEditEdgeId) {
      // Indicar visualmente que estamos en modo edición
      console.log('[useEdgeGeometryEdit] Geometry edit mode active for edge:', geometryEditEdgeId);
    }
  }, [geometryEditEdgeId]);

  // ═══════════════════════════════════════════════════════════════════════════
  // CLEANUP: Salir del modo geométrico cuando se desactiva la edición de red
  // Esto cubre: cerrar sidebar, cambiar herramienta, desactivar isNetworkInteractive
  // ═══════════════════════════════════════════════════════════════════════════
  const urbanNetwork = useUrbanNetworkSafe();
  const isNetworkInteractive = urbanNetwork?.isNetworkInteractive ?? false;
  const prevIsNetworkInteractiveRef = useRef(isNetworkInteractive);

  useEffect(() => {
    // Detectar transición de true → false
    if (prevIsNetworkInteractiveRef.current && !isNetworkInteractive) {
      if (geometryEditEdgeId) {
        console.log('[useEdgeGeometryEdit] 🔌 Network mode deactivated, exiting geometry edit');
        cancelGeometryEditRef.current();
      }
    }
    prevIsNetworkInteractiveRef.current = isNetworkInteractive;
  }, [isNetworkInteractive, geometryEditEdgeId]);
}

/**
 * Hook para activar edición geométrica con doble-click en edge
 *
 * Cuando el usuario hace doble-click sobre un edge de la red canónica,
 * activa el modo de edición geométrica para ese edge.
 */
export function useEdgeGeometryEditOnDoubleClick(): void {
  const { startGeometryEdit, geometryEditEdgeId } = useNetworkEdit();

  // Refs para evitar stale closures - patrón usado en useNetworkEditInteractivity
  const startGeometryEditRef = useRef(startGeometryEdit);
  const geometryEditEdgeIdRef = useRef(geometryEditEdgeId);
  const handlerRef = useRef<((e: any) => void) | null>(null);

  // Mantener refs sincronizados con el estado
  useEffect(() => {
    startGeometryEditRef.current = startGeometryEdit;
  }, [startGeometryEdit]);

  useEffect(() => {
    geometryEditEdgeIdRef.current = geometryEditEdgeId;
  }, [geometryEditEdgeId]);

  // Efecto principal: registrar listener cuando el mapa está listo
  useEffect(() => {
    console.log('[useEdgeGeometryEditOnDoubleClick] 🎬 useEffect starting...');

    let pollInterval: ReturnType<typeof setInterval> | null = null;
    let cleanupFn: (() => void) | null = null;

    // Función para registrar el handler de dblclick
    const registerHandler = (map: any): void => {
      console.log('[useEdgeGeometryEditOnDoubleClick] ✅ Registering dblclick handler');
      console.log('[useEdgeGeometryEditOnDoubleClick] 📊 doubleClickZoom enabled:', map.doubleClickZoom.isEnabled());

      // CRÍTICO: Desactivar doubleClickZoom para que nuestro handler reciba el evento
      if (map.doubleClickZoom.isEnabled()) {
        map.doubleClickZoom.disable();
        console.log('[useEdgeGeometryEditOnDoubleClick] 🔒 Disabled doubleClickZoom');
      }

      // Handler de doble-click
      const handleDblClick = (e: any): void => {
        console.log('[useEdgeGeometryEditOnDoubleClick] 🖱️ === DBLCLICK EVENT RECEIVED ===');

        // Usar refs para valores actuales (evita stale closure)
        const currentGeometryEditEdgeId = geometryEditEdgeIdRef.current;

        // Verificar si la capa existe
        const layerExists = !!map.getLayer('canonical-network-edges');
        console.log('[useEdgeGeometryEditOnDoubleClick] 📊 Event details:', {
          point: e.point,
          lngLat: e.lngLat,
          layerExists,
          currentGeometryEditEdgeId,
        });

        // Si ya estamos editando, ignorar
        if (currentGeometryEditEdgeId) {
          console.log('[useEdgeGeometryEditOnDoubleClick] ⏭️ Already editing edge, ignoring');
          return;
        }

        if (!layerExists) {
          console.warn('[useEdgeGeometryEditOnDoubleClick] ⚠️ Layer canonical-network-edges does not exist!');
          return;
        }

        // Buscar edge bajo el cursor
        const features = map.queryRenderedFeatures(e.point, {
          layers: ['canonical-network-edges'],
        });

        console.log('[useEdgeGeometryEditOnDoubleClick] 🔍 Query result:', {
          featuresFound: features.length,
          edgeIds: features.map((f: any) => f.properties?.id),
        });

        if (features.length > 0) {
          const edgeId = features[0].properties?.id as string;
          if (edgeId) {
            console.log('[useEdgeGeometryEditOnDoubleClick] ✅ Starting geometry edit for edge:', edgeId);

            // Prevenir cualquier comportamiento default
            e.preventDefault();

            // Iniciar edición geométrica usando ref actual
            startGeometryEditRef.current(edgeId);
          } else {
            console.log('[useEdgeGeometryEditOnDoubleClick] ⚠️ Edge feature has no id property');
          }
        } else {
          console.log('[useEdgeGeometryEditOnDoubleClick] ℹ️ No edge under cursor');
        }
      };

      // Guardar handler en ref para cleanup
      handlerRef.current = handleDblClick;

      // Registrar listener
      map.on('dblclick', handleDblClick);
      console.log('[useEdgeGeometryEditOnDoubleClick] 📝 dblclick listener registered on map');

      // Configurar cleanup específico
      cleanupFn = () => {
        console.log('[useEdgeGeometryEditOnDoubleClick] 🧹 Removing dblclick listener');
        map.off('dblclick', handleDblClick);
      };
    };

    // Intentar obtener el mapa
    const map = mapEngine.getMap();

    if (map) {
      // Mapa disponible inmediatamente
      console.log('[useEdgeGeometryEditOnDoubleClick] ✅ Map immediately available');
      registerHandler(map);
    } else {
      // Mapa no disponible - usar polling
      console.log('[useEdgeGeometryEditOnDoubleClick] ⏳ Map not ready, starting polling...');

      pollInterval = setInterval(() => {
        const m = mapEngine.getMap();
        if (m) {
          console.log('[useEdgeGeometryEditOnDoubleClick] ✅ Map became available via polling');
          if (pollInterval) {
            clearInterval(pollInterval);
            pollInterval = null;
          }
          registerHandler(m);
        }
      }, 100);
    }

    // Cleanup
    return () => {
      console.log('[useEdgeGeometryEditOnDoubleClick] 🧹 Cleanup starting...');

      if (pollInterval) {
        clearInterval(pollInterval);
      }

      if (cleanupFn) {
        cleanupFn();
      }

      console.log('[useEdgeGeometryEditOnDoubleClick] ✅ Cleanup complete');
    };
  }, []); // Sin dependencias - registrar UNA SOLA VEZ
}
